<template>
    <div>
        <div class="mb-6 -mt-4">
            <h1>Employment Details</h1>
        </div>

        <ValidationObserver ref="employmentDetailsObserver">
            <div class="mb-6">
                <DropdownComponent
                    :options="collections.employment_statuses"
                    name="employment_status"
                    label="Employment Status"
                    placeholder="- Please Select -"
                    v-model="employmentDetails.employment_status"
                    @change="updateEmploymentDetails"
                    :searchable="false"
                />
            </div>

            <div class="mb-6" v-if="isSelfEmployed">
                <InputComponent
                    label="Nature of Self Employment"
                    type="text"
                    placeholder="E.g. Construction or Electrician"
                    name="nature_of_self_employment"
                    v-model="employmentDetails.nature_of_self_employment"
                    @change="updateEmploymentDetails"
                />
            </div>

            <div class="mb-6">
                <InputComponent
                    label="Employer's Name"
                    type="text"
                    placeholder="Employer's Name"
                    name="employer_name"
                    v-model="employmentDetails.employer_name"
                    @change="updateEmploymentDetails"
                />
            </div>

            <div class="mb-6">
                <InputComponent
                    label="Occupation/Position"
                    type="text"
                    placeholder="Occupation/Position"
                    name="occupation_position"
                    v-model="employmentDetails.occupation"
                    @change="updateEmploymentDetails"
                />
            </div>

            <div class="mb-6">
                <YesNoSliderComponent
                    name="pep"
                    label="Do you consider yourself a PEP (Politically Exposed Person)?"
                    v-model="isPEP"
                    @change="setIsPEP"
                    hint="The United Nations Convention against Corruption (UNCAC) defines PEP's as being individuals who are, or have been, entrusted with prominent functions, or whose family members and close associates have been so entrusted. Examples are: Heads of State or Government, senior politicians, senior government, judicial or military officials, senior executives of state owned corporations or important political party officials."
                />
            </div>

            <transition
                enter-active-class="transition ease-out duration-500 transform"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-out duration-200 transform"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div v-if="isPEP" class="mb-6">
                    <InputComponent
                        label="If Yes, Provide Details"
                        type="text"
                        placeholder="If Yes, Provide Details"
                        name="self_employment_details"
                        v-model="employmentDetails.politically_exposed_person_details"
                        @change="updateEmploymentDetails"
                    />
                </div>
            </transition>

            <div class="mb-6 w-1/2">
                <InputComponent
                    label="Years at Current Employer"
                    type="integer"
                    placeholder="Years"
                    name="years_at_current_employer"
                    rules="required|integer"
                    v-model="employmentDetails.years_at_employer"
                    @change="updateEmploymentDetails"
                />
            </div>

            <div class="mb-6">
                <YesNoSliderComponent
                    name="professional_qualification"
                    label="Have you attended any training or do you have a professional qualification that would assist your understanding of the risks of trading leveraged products?"
                    v-model="hasProfessionalQualification"
                    @change="setHasProfessionalQualification"
                />
            </div>

            <transition
                enter-active-class="transition ease-out duration-500 transform"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-out duration-200 transform"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div v-if="hasProfessionalQualification" class="mb-6">
                    <InputComponent
                        label="Please provide details"
                        type="text"
                        placeholder="Please provide details"
                        name="professional_qualification_details"
                        v-model="employmentDetails.professional_qualification_or_training_details"
                        @change="updateEmploymentDetails"
                    />
                </div>
            </transition>
        </ValidationObserver>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ValidationObserver } from "vee-validate";
import InputComponent from "@components/inputs/InputComponent";
import DropdownComponent from "@components/inputs/DropdownComponent";
import YesNoSliderComponent from "@components/inputs/YesNoSliderComponent";
import stepperValidationService from "@services/stepperValidationService";

export default {
    name: "EmploymentDetailsFormComponent",
    components: {
        ValidationObserver,
        InputComponent,
        DropdownComponent,
        YesNoSliderComponent,
    },
    props: {
        step: { default: "" },
    },
    data() {
        return {
            employmentDetails: {},
            isPEP: false,
            hasProfessionalQualification: false,
            isFormValidated: false,
        };
    },
    computed: {
        ...mapGetters(["employmentDetailsState", "collections"]),
        isSelfEmployed() {
            return this.employmentDetails.employment_status == "Self Employed";
        },
    },
    methods: {
        ...mapActions(["doGetEmploymentDetails", "doUpdateEmploymentDetails", "doGetCollectionData"]),
        updateEmploymentDetails() {
            this.doUpdateEmploymentDetails(this.employmentDetails);
        },
        checkIsPEP() {
            this.isPEP = !!this.employmentDetails.politically_exposed_person_details;
        },
        checkHasProfessionalQualification() {
            this.hasProfessionalQualification = !!this.employmentDetails.professional_qualification_or_training_details;
        },
        setIsPEP(isPEP = false) {
            if (!isPEP) {
                this.employmentDetails.politically_exposed_person_details = null;
                this.updateEmploymentDetails();
            }
            this.isPEP = isPEP;
        },
        setHasProfessionalQualification(hasProfessionalQualification = false) {
            if (!hasProfessionalQualification) {
                this.employmentDetails.professional_qualification_or_training_details = null;
                this.updateEmploymentDetails();
            }
            this.hasProfessionalQualification = hasProfessionalQualification;
        },
        doValidateForm() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.employmentDetails,
                this.$refs.employmentDetailsObserver,
                false,
                true
            );
        },
    },
    mounted() {
        this.$root.$on("submittingApplication", () => this.doValidateForm());
        this.$root.$on("stepChanged", (step) => {
            if (this.step < step && !this.isFormValidated) {
                this.doValidateForm();
                this.isFormValidated = true;
            } else if (this.step == step) {
                this.isFormValidated = false;
            }
        });

        this.doGetEmploymentDetails().then((response) => (this.employmentDetails = response));
        this.checkIsPEP();
        this.checkHasProfessionalQualification();
        this.doGetCollectionData("employment_statuses");

        this.$gtm.trackView('Employment Details', 'currentPath');
    },
    watch: {
        employmentDetails() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.employmentDetails,
                this.$refs.employmentDetailsObserver
            );
        },
    },
};
</script>
